import * as React from 'react';
import {
  AccessibilityInfo,
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import {noop} from 'lodash';
import PropTypes from 'prop-types';

import {AccessibilityWrapper} from '/component/layout/AccessibilityWrapper';
import {indent} from '/constant/layout.constant';
import {screenWidth as getScreenWidth} from '/constant/layout.constant';
import {withCompetency, withTheme} from '/provider/ThemeProvider';
import {withHOC} from '/util/hoc.util';

import KeyboardFocusable from '../ExtKeyboard/KeyboardFocusable';

import styles from './Carousel.style';

interface CarouselProps {
  children?: React.ReactNode;
  getTheme?: (key: string) => any;
  screenWidth?: number | null;
  snapToInterval?: number;
  cardTitles?: string[];
  restrictAccessibilityToActiveSlide?: boolean;
  indicatorDots?: boolean;
  webFitContainer?: boolean;
  webGap?: number;
}

interface CarouselState {
  scroll: Animated.Value;
  animatingRight: boolean;
  activeIndex: number;
  focusedIndex: number | null;
  containerWidth: number | null;
}

export class Carousel extends React.Component<CarouselProps, CarouselState> {
  dragging: boolean;

  lastValue: number;

  scrollRef: React.RefObject<ScrollView>;

  static propTypes = {
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
    ]),
    getTheme: PropTypes.func,
    screenWidth: PropTypes.number,
    snapToInterval: PropTypes.number,
    cardTitles: PropTypes.arrayOf(PropTypes.string),
    restrictAccessibilityToActiveSlide: PropTypes.bool,
    indicatorDots: PropTypes.bool,
    webFitContainer: PropTypes.bool,
    webGap: PropTypes.number,
  };

  static defaultProps = {
    children: [],
    getTheme: noop,
    screenWidth: null,
    snapToInterval:
      Platform.OS === 'web'
        ? getScreenWidth() - 100
        : getScreenWidth() - indent * 1.75,
    cardTitles: undefined,
    restrictAccessibilityToActiveSlide: false,
    indicatorDots: true,
    webFitContainer: false,
    webGap: undefined,
  };

  constructor(props: CarouselProps) {
    super(props);

    this.lastValue = 0;
    this.dragging = false;
    this.scrollRef = React.createRef();

    this.state = {
      scroll: new Animated.Value(0),
      animatingRight: true,
      activeIndex: 0,
      focusedIndex: null,
      containerWidth: null,
    };

    this.isActive = this.isActive.bind(this);
    this.onWrapperLayout = this.onWrapperLayout.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onScrollBegin = this.onScrollBegin.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.renderDots = this.renderDots.bind(this);
  }

  override componentDidMount() {
    const {scroll} = this.state;

    scroll.addListener(({value}: {value: number}) => {
      if (this.dragging) return;

      this.dragging = true;
      this.setState({animatingRight: value > this.lastValue});
      this.lastValue = value;
    });
  }

  isActive(current: number, index: number) {
    const {animatingRight} = this.state;
    if (current % 2 === 0) {
      return current / 2 === index;
    }

    if (animatingRight) {
      return Math.floor(current / 2) === index;
    }

    return Math.ceil(current / 2) === index;
  }

  onScrollBegin(evt: NativeSyntheticEvent<NativeScrollEvent>) {
    if (this.dragging) return;
    this.lastValue = evt.nativeEvent.contentOffset.x;
  }

  onWrapperLayout(evt: {nativeEvent: {layout: {width: number}}}) {
    if (Platform.OS === 'web') {
      const {width} = evt.nativeEvent.layout;
      this.setState({containerWidth: width});
    }
  }

  onScrollEnd() {
    this.dragging = false;
  }

  onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetX = event.nativeEvent.contentOffset.x;
    const {containerWidth} = this.state;
    const {snapToInterval} = this.props;

    let activeIndex: number;
    if (Platform.OS === 'web' && containerWidth != null) {
      const offsets = this.getWebSnapOffsets(containerWidth);
      const closest =
        offsets.length > 0
          ? offsets.reduce(
              (best, _, i) =>
                Math.abs((offsets[i] ?? 0) - offsetX) <
                Math.abs((offsets[best] ?? 0) - offsetX)
                  ? i
                  : best,
              0,
            )
          : 0;
      activeIndex = closest;
    } else {
      activeIndex = Math.round(offsetX / (snapToInterval || 1));
    }

    if (activeIndex !== this.state.activeIndex) {
      this.setState({activeIndex});
    }
  };

  getWebSnapOffsets(containerWidth: number): number[] {
    const {children, snapToInterval, webFitContainer} = this.props;
    const childArray = Array.isArray(children) ? children : [children];
    const num = childArray.length;
    if (webFitContainer) {
      return Array.from({length: num}, (_, i) => i * containerWidth);
    }
    const step = snapToInterval || containerWidth - 60;
    const peekLeft = snapToInterval ? 0 : 20;
    const offsets: number[] = [];
    for (let i = 0; i < num; i++) {
      offsets.push(Math.max(0, i * step - peekLeft));
    }
    return offsets;
  }

  renderDots(
    num: any,
    width: any,
    competencyColor: any,
    snapToOffsets?: number[],
  ) {
    const {scroll} = this.state;
    const {focusedIndex} = this.state;
    const {cardTitles, indicatorDots} = this.props;

    if (!indicatorDots) {
      return null;
    }

    const useOffsets = snapToOffsets != null && snapToOffsets.length === num;
    const inputRange = useOffsets
      ? [...snapToOffsets]
      : (() => {
          const range = [0];
          for (let i = 1; i < num; i++) {
            range.push((i - 0.5) * width);
            range.push(i * width);
          }
          return range;
        })();
    const scrollToX = (index: number) =>
      useOffsets ? snapToOffsets[index] : index * width;

    const children = [];

    for (let i = 0; i < num; i++) {
      const isSelected = this.state.activeIndex === i;
      const isFocused = focusedIndex === i;

      const rawTitle = cardTitles?.[i];

      const baseTitle =
        rawTitle && rawTitle.trim().length > 0 ? rawTitle : `slide ${i + 1}`;

      const accessibilityLabel = isSelected
        ? `Viewing ${baseTitle}, slide ${i + 1} of ${num}`
        : `${baseTitle}, slide ${i + 1} of ${num}`;

      children.push(
        <KeyboardFocusable
          key={i}
          accessible={true}
          focusable={true}
          disableFocusRing={true}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel}
          // accessibilityState={{selected: this.state.activeIndex === i}}
          onPress={() => {
            this.scrollRef.current?.scrollTo({
              x: scrollToX(i),
              animated: true,
            });

            this.setState({activeIndex: i}, () => {
              AccessibilityInfo.announceForAccessibility(
                `${baseTitle}, slide ${i + 1} of ${num}, Selected`,
              );
            });
          }}
          onFocus={() => this.setState({focusedIndex: i})}
          onBlur={() => this.setState({focusedIndex: null})}
          style={[styles.dotButton, isFocused && styles.dotButtonFocused]}
          activeOpacity={0.7}>
          <View
            style={[
              styles.dotBorder,
              {borderColor: competencyColor.backgroundColor || 'transparent'},
            ]}>
            <Animated.View
              style={[
                styles.dot,
                competencyColor,
                {
                  opacity: scroll.interpolate({
                    inputRange,
                    outputRange: inputRange.map((_, cur) =>
                      useOffsets
                        ? cur === i
                          ? 1
                          : 0.1
                        : this.isActive(cur, i)
                        ? 1
                        : 0.1,
                    ),
                    extrapolate: 'clamp',
                  }),
                },
              ]}
              accessible={Platform.OS === 'ios' && false}
            />
          </View>
        </KeyboardFocusable>,
      );
    }

    // Make the container accessible for keyboard navigation
    return (
      <View
        style={styles.dots}
        accessible={false}
        accessibilityRole="tablist"
        accessibilityLabel="Carousel navigation - tap dots to navigate without dragging">
        {children}
      </View>
    );
  }

  override render() {
    const {
      children: _children,
      getTheme,
      snapToInterval: snapToIntervalProp,
      restrictAccessibilityToActiveSlide,
      indicatorDots,
      webFitContainer,
      webGap,
    } = this.props;
    const childArray = Array.isArray(_children) ? _children : [_children];
    const {containerWidth} = this.state;
    const snapToOffsets =
      Platform.OS === 'web' && containerWidth != null
        ? this.getWebSnapOffsets(containerWidth)
        : undefined;
    const snapToInterval =
      Platform.OS === 'web' && containerWidth != null
        ? undefined
        : snapToIntervalProp ?? getScreenWidth() - indent * 1.75;

    const contentContainerStyle =
      Platform.OS === 'web' && webFitContainer
        ? styles.contentContainerWebFit
        : [
            styles.contentContainer,
            Platform.OS === 'web' && webGap != null ? {gap: webGap} : null,
          ];

    return (
      <View
        style={[styles.wrapper, !indicatorDots && styles.wrapperNoDots]}
        onLayout={this.onWrapperLayout}>
        <Animated.ScrollView
          ref={this.scrollRef}
          horizontal
          bounces
          showsHorizontalScrollIndicator={false}
          snapToInterval={snapToOffsets ? undefined : snapToInterval}
          snapToOffsets={snapToOffsets}
          decelerationRate="fast"
          scrollEventThrottle={16}
          contentContainerStyle={contentContainerStyle}
          onMomentumScrollEnd={this.onScrollEnd}
          onScrollBeginDrag={this.onScrollBegin}
          onMomentumScrollBegin={this.onScrollBegin}
          onScroll={Animated.event(
            [
              {
                nativeEvent: {
                  contentOffset: {x: this.state.scroll},
                },
              },
            ],
            {useNativeDriver: true, listener: this.onScroll},
          )}>
          {childArray.map((child, i) => {
            const isActiveSlide = this.state.activeIndex === i;

            const accessibilityProps = restrictAccessibilityToActiveSlide
              ? {
                  accessibilityElementsHidden: !isActiveSlide,
                  importantForAccessibility: isActiveSlide
                    ? 'yes'
                    : 'no-hide-descendants',
                  focusable: isActiveSlide,
                }
              : {};

            return (
              <AccessibilityWrapper
                key={i}
                {...accessibilityProps}
                style={
                  Platform.OS === 'web' && webFitContainer && containerWidth
                    ? {
                        width: containerWidth,
                        paddingHorizontal: indent * 0.35,
                        boxSizing: 'border-box',
                      }
                    : undefined
                }>
                {child}
              </AccessibilityWrapper>
            );
          })}
        </Animated.ScrollView>

        {this.renderDots(
          childArray.length,
          snapToInterval || 0,
          getTheme?.('competency.backgroundColor') || {},
          snapToOffsets,
        )}
      </View>
    );
  }
}

export default withHOC(withTheme, withCompetency)(Carousel);
